import { PlusOutlined } from '@ant-design/icons';
import { Button, Input, Space, Table, Typography } from 'antd';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { t } from 'ttag';

import useSyncWithUrlSearchParams from 'common/hooks/useSyncWithUrlSearchParams';
import { useTableQuery } from 'common/hooks/useTableQuery';

import CreateModal from './CreateModal';
import NewOrderModal from './NewOrderModal';
import { OrganizationListQueryKey } from './queries';

const { Search } = Input;

const SearchBox = styled(Search)`
  && {
    width: 350px;
  }
`;

const Organizations = () => {
  const [query] = useSyncWithUrlSearchParams();
  const [search, setSearch] = useState(query.search || '');
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [selectedOrganizationForOrder, setSelectedOrganizationForOrder] = useState(null);

  const sortOrder = name => {
    const hasSort = query.sort?.includes(name) ?? false;

    if (!hasSort) {
      return false;
    }

    return query.sort.startsWith('-') ? 'descend' : 'ascend';
  };

  const handleNewOrder = organization => {
    setSelectedOrganizationForOrder(organization);
  };

  const columns = [
    {
      key: 'name',
      dataIndex: 'name',
      title: t`Name`,
      sorter: true,
      sortOrder: sortOrder('name'),
      render: (name, organization) => (
        <Link to={`/organization/${organization.id}/events`}>{name}</Link>
      ),
    },
    {
      key: 'actions',
      title: t`Actions`,
      width: 120,
      render: (_, organization) => (
        <Typography.Link onClick={() => handleNewOrder(organization)}>
          {t`New Order`}
        </Typography.Link>
      ),
    },
  ];

  const { tableProps } = useTableQuery(
    apiParams => ({
      queryKey: OrganizationListQueryKey.getQueryKey(search, apiParams),
      queryFn: OrganizationListQueryKey.queryFn,
    }),
    {
      columns,
      rowKey: 'shortcode',
    },
    search,
  );

  return (
    <>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBlock: 20 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setIsCreateModalVisible(true)}
        >
          {t`Add Organization`}
        </Button>
        <SearchBox
          defaultValue={search}
          placeholder={t`Search Organizations...`}
          disabled={tableProps.loading}
          onSearch={value => setSearch(value)}
        />
      </Space>

      <Table {...tableProps} />

      <CreateModal visible={isCreateModalVisible} onClose={() => setIsCreateModalVisible(false)} />

      {!!selectedOrganizationForOrder && (
        <NewOrderModal
          organization={selectedOrganizationForOrder}
          onClose={() => setSelectedOrganizationForOrder(null)}
        />
      )}
    </>
  );
};

export default Organizations;
